import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import SpotifyWebApi from "spotify-web-api-js";
import { useTokenContext } from "../auth/UserTokenProvider";
import config from "../config";
import {
  createRequestGate,
  getPlaybackPollDelay,
  parseRetryAfterMs
} from "../hooks/playbackPolling";
import { subscribeToPlaybackSync } from "../music/playbackSync";
import { CurrentlyPlayingState, PlayingStates } from "../types/currentlyPlayingState";

interface SpotifyRequestError {
  status?: number;
  responseText?: string;
  getResponseHeader?: (header: string) => string | null;
}

const CurrentlyPlayingContext = createContext<CurrentlyPlayingState | undefined>(undefined);

const fetchCurrentTrack = (tokenValue: string): Promise<SpotifyApi.CurrentlyPlayingObject | ""> => {
  const spotifyApi = new SpotifyWebApi();
  spotifyApi.setAccessToken(tokenValue);
  return spotifyApi.getMyCurrentPlayingTrack();
};

const getPlaybackState = (
  currentlyPlayingObject: SpotifyApi.CurrentlyPlayingObject | ""
): CurrentlyPlayingState => {
  if (currentlyPlayingObject === "") {
    return { state: PlayingStates.NotPlaying, currentlyPlayingObject: null };
  }

  return {
    state: currentlyPlayingObject.is_playing ? PlayingStates.Playing : PlayingStates.Paused,
    currentlyPlayingObject
  };
};

export const CurrentlyPlayingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, clearToken, refreshToken } = useTokenContext();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<CurrentlyPlayingState>({
    state: PlayingStates.Loading,
    currentlyPlayingObject: null
  });
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");
  const [completedRequestCount, setCompletedRequestCount] = useState(0);
  const tokenRef = useRef(token);
  const currentlyPlayingRef = useRef(currentlyPlaying);
  const rateLimitedUntilRef = useRef(0);
  const requestSequenceRef = useRef(0);
  const lastAppliedRequestRef = useRef(0);
  const requestRef = useRef<() => Promise<void>>(async () => {});
  const gateRef = useRef<(() => Promise<void>) | null>(null);

  tokenRef.current = token;
  currentlyPlayingRef.current = currentlyPlaying;

  const setLatestPlayback = useCallback(
    (sequence: number, value: SpotifyApi.CurrentlyPlayingObject | "") => {
      if (sequence < lastAppliedRequestRef.current) return;
      lastAppliedRequestRef.current = sequence;
      const nextState = getPlaybackState(value);
      currentlyPlayingRef.current = nextState;
      setCurrentlyPlaying(nextState);
    },
    []
  );

  const showPlaybackError = useCallback((request: SpotifyRequestError) => {
    const toastId = "failed-song";
    if (!toast.isActive(toastId)) {
      toast.error(request.responseText || "Failed to update the current song", {
        position: "bottom-center",
        toastId,
        autoClose: 5000,
        onClick: () => toast.dismiss()
      });
    }
  }, []);

  const expireSession = useCallback(async () => {
    const toastId = "session-expired";
    if (!toast.isActive(toastId)) {
      toast.error("Session expired. Please log in again.", {
        position: "bottom-center",
        toastId,
        autoClose: 3000,
        onClick: () => toast.dismiss()
      });
    }
    await clearToken();
  }, [clearToken]);

  const handleNonAuthError = useCallback(
    (request: SpotifyRequestError) => {
      if (request.status === 429) {
        rateLimitedUntilRef.current =
          Date.now() + parseRetryAfterMs(request.getResponseHeader?.("Retry-After") ?? null, 30000);
        return;
      }

      // Network and server failures are transient. Keep the last good track visible.
      showPlaybackError(request);
      if (
        request.status !== undefined &&
        request.status < 500 &&
        currentlyPlayingRef.current.currentlyPlayingObject === null
      ) {
        const errorState: CurrentlyPlayingState = {
          state: PlayingStates.Error,
          currentlyPlayingObject: null
        };
        currentlyPlayingRef.current = errorState;
        setCurrentlyPlaying(errorState);
      }
    },
    [showPlaybackError]
  );

  const requestPlayback = useCallback(async () => {
    const activeToken = tokenRef.current;
    if (
      !activeToken ||
      document.visibilityState !== "visible" ||
      Date.now() < rateLimitedUntilRef.current
    ) {
      return;
    }

    const sequence = ++requestSequenceRef.current;
    try {
      const result = await fetchCurrentTrack(activeToken.value);
      if (tokenRef.current?.value === activeToken.value) {
        setLatestPlayback(sequence, result);
      }
      return;
    } catch (error) {
      const request = error as SpotifyRequestError;
      if (request.status !== 401) {
        handleNonAuthError(request);
        return;
      }
    }

    // A scheduled refresh may have replaced the rejected token while playback was in flight.
    const latestToken = tokenRef.current;
    let refreshed = latestToken?.value !== activeToken.value ? latestToken : null;
    if (!refreshed) {
      try {
        refreshed = await refreshToken();
      } catch (error) {
        const request = error as SpotifyRequestError;
        if (request.status === 401 || request.status === 403) {
          await expireSession();
        } else {
          handleNonAuthError(request);
        }
        return;
      }
    }

    if (!refreshed) {
      await expireSession();
      return;
    }

    tokenRef.current = refreshed;
    try {
      const result = await fetchCurrentTrack(refreshed.value);
      if (tokenRef.current?.value === refreshed.value) {
        setLatestPlayback(sequence, result);
      }
    } catch (error) {
      const request = error as SpotifyRequestError;
      if (request.status === 401) {
        await expireSession();
      } else {
        handleNonAuthError(request);
      }
    }
  }, [expireSession, handleNonAuthError, refreshToken, setLatestPlayback]);

  requestRef.current = requestPlayback;
  if (gateRef.current === null) {
    gateRef.current = createRequestGate(() => requestRef.current());
  }

  const syncRef = useRef<() => Promise<void>>(async () => {});
  syncRef.current = gateRef.current;

  const syncNow = useCallback(() => {
    void syncRef
      .current()
      .catch(error => {
        showPlaybackError(error as SpotifyRequestError);
      })
      .finally(() => {
        setCompletedRequestCount(count => count + 1);
      });
  }, [showPlaybackError]);

  useEffect(() => {
    if (token !== null) {
      syncNow();
    } else {
      const loadingState: CurrentlyPlayingState = {
        state: PlayingStates.Loading,
        currentlyPlayingObject: null
      };
      currentlyPlayingRef.current = loadingState;
      setCurrentlyPlaying(loadingState);
      rateLimitedUntilRef.current = 0;
    }
  }, [syncNow, token]);

  // Schedule the next heartbeat after the previous request settles, so slow requests never stack.
  useEffect(() => {
    const pollDelay = getPlaybackPollDelay(
      currentlyPlaying.state,
      isVisible,
      config.client.trackCheckDelaySeconds * 1000
    );
    if (token === null || pollDelay === null) return;

    const rateLimitDelay = Math.max(0, rateLimitedUntilRef.current - Date.now());
    const timeoutId = window.setTimeout(syncNow, Math.max(pollDelay, rateLimitDelay));

    return () => window.clearTimeout(timeoutId);
  }, [completedRequestCount, currentlyPlaying.state, isVisible, syncNow, token]);

  useEffect(() => {
    if (
      currentlyPlaying.state === PlayingStates.Playing &&
      currentlyPlaying.currentlyPlayingObject.item !== null
    ) {
      const timeToRefresh =
        currentlyPlaying.currentlyPlayingObject.item.duration_ms -
        (currentlyPlaying.currentlyPlayingObject.progress_ms === null
          ? 0
          : currentlyPlaying.currentlyPlayingObject.progress_ms) +
        500;
      const timeoutId = window.setTimeout(syncNow, Math.max(timeToRefresh, 0));
      return () => window.clearTimeout(timeoutId);
    }
  }, [currentlyPlaying, syncNow]);

  // Resume and app-control events share the same request gate and collapse while a sync is active.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === "visible";
      setIsVisible(visible);
      if (visible) syncNow();
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted || document.visibilityState === "visible") {
        syncNow();
      }
    };
    const handleOnline = () => {
      if (document.visibilityState === "visible") syncNow();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("online", handleOnline);
    const unsubscribeFromPlaybackSync = subscribeToPlaybackSync(syncNow);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("online", handleOnline);
      unsubscribeFromPlaybackSync();
    };
  }, [syncNow]);

  return (
    <CurrentlyPlayingContext.Provider value={currentlyPlaying}>
      {children}
    </CurrentlyPlayingContext.Provider>
  );
};

export const useCurrentlyPlayingContext = (): CurrentlyPlayingState => {
  const context = React.useContext(CurrentlyPlayingContext);
  if (context === undefined) {
    throw new Error("useCurrentlyPlayingSong must be used within a CurrentlyPlayingProvider");
  }
  return context;
};
